import {
    warnOnce
} from "../../utils/development";
import defaultJSON from "../../variants/themes/default.json";

export const mergeSpaces = (
    defaultSpacesTokens: NCoreUIKit.Spaces,
    projectSpacesTokens: NCoreUIKit.Spaces
) => {
    return {
        ...defaultSpacesTokens,
        ...projectSpacesTokens
    };
};

export const mergeRadiuses = (
    defaultRadiusesTokens: NCoreUIKit.Radiuses,
    projectRadiusesTokens: NCoreUIKit.Radiuses
) => {
    return {
        ...defaultRadiusesTokens,
        ...projectRadiusesTokens
    };
};

export const mergeBorders = (
    defaultBordersTokens: NCoreUIKit.Borders,
    projectBordersTokens: NCoreUIKit.Borders & {
        seperator?: number;
    }
) => {
    const {
        seperator,
        ...currentProjectBordersTokens
    } = projectBordersTokens;

    if(seperator !== undefined) {
        warnOnce({
            message: "Theme: borders \"seperator\" is deprecated and will be removed in the next major version. Use \"separator\" instead."
        });
    }

    return {
        ...defaultBordersTokens,
        ...(seperator !== undefined ? {
            separator: seperator
        } : {}),
        ...currentProjectBordersTokens
    };
};

export const mergeInlineSpaces = (
    defaultInlineSpacesTokens: NCoreUIKit.InlineSpaces,
    projectInlineSpacesTokens: NCoreUIKit.InlineSpaces
) => {
    return {
        ...defaultInlineSpacesTokens,
        ...projectInlineSpacesTokens
    };
};

export const mergeShapeConfigs = (
    defaultShapeConfigsTokens: NCoreUIKit.ShapeConfigs,
    projectShapeConfigsTokens: NCoreUIKit.ShapeConfigs
) => {
    return {
        ...defaultShapeConfigsTokens,
        ...projectShapeConfigsTokens,
        chartHeight: {
            ...defaultShapeConfigsTokens.chartHeight,
            ...projectShapeConfigsTokens.chartHeight
        }
    };
};

export const mergeControlHeights = (
    defaultControlHeightsTokens: NCoreUIKit.ControlHeights,
    projectControlHeightsTokens: NCoreUIKit.ControlHeights
) => {
    return {
        ...defaultControlHeightsTokens,
        ...projectControlHeightsTokens
    };
};

export const mergeControlPaddings = (
    defaultControlPaddingsTokens: NCoreUIKit.ControlPaddings,
    projectControlPaddingsTokens: NCoreUIKit.ControlPaddings
) => {
    return {
        ...defaultControlPaddingsTokens,
        ...projectControlPaddingsTokens
    };
};

export const mergeIconSizes = (
    defaultIconSizesTokens: NCoreUIKit.IconSizes,
    projectIconSizesTokens: NCoreUIKit.IconSizes
) => {
    return {
        ...defaultIconSizesTokens,
        ...projectIconSizesTokens
    };
};

export const mergeAvatarSizes = (
    defaultAvatarSizesTokens: NCoreUIKit.AvatarSizes,
    projectAvatarSizesTokens: NCoreUIKit.AvatarSizes
) => {
    return {
        ...defaultAvatarSizesTokens,
        ...projectAvatarSizesTokens
    };
};

export const mergeTouchTargets = (
    defaultTouchTargetsTokens: NCoreUIKit.TouchTargets,
    projectTouchTargetsTokens: NCoreUIKit.TouchTargets
) => {
    return {
        ...defaultTouchTargetsTokens,
        ...projectTouchTargetsTokens
    };
};

export const mergeElevations = (
    defaultElevationsTokens: NCoreUIKit.Elevations,
    projectElevationsTokens: NCoreUIKit.Elevations
) => {
    return {
        ...defaultElevationsTokens,
        ...projectElevationsTokens
    };
};

export const mergeShadowAlpha = (
    defaultShadowAlphaTokens: NCoreUIKit.ShadowAlphaWithVariants,
    projectShadowAlphaTokens: NCoreUIKit.ShadowAlphaWithVariants
) => {
    return {
        ...defaultShadowAlphaTokens,
        ...projectShadowAlphaTokens
    };
};

export const mergeDurations = (
    defaultDurationsTokens: NCoreUIKit.Durations,
    projectDurationsTokens: NCoreUIKit.Durations
) => {
    return {
        ...defaultDurationsTokens,
        ...projectDurationsTokens
    };
};

export const mergeShapes = ({
    defaultShapes,
    projectShapes
}: {
    defaultShapes: NCoreUIKit.Shapes;
    projectShapes: NCoreUIKit.Shapes;
}) => {
    const defaultGapPropagations = Object.keys(defaultJSON.shapes.spaces) as (keyof NCoreUIKit.SpacesWithVariants)[];
    const defaultSharpness = Object.keys(defaultJSON.shapes.radiuses) as (keyof NCoreUIKit.RadiusesWithVariants)[];

    const defaultControlPaddings = defaultShapes.controlPaddings;
    const defaultControlHeights = defaultShapes.controlHeights;
    const defaultInlineSpaces = defaultShapes.inlineSpaces;
    const defaultShapeConfigs = defaultShapes.shapeConfigs;
    const defaultTouchTargets = defaultShapes.touchTargets;
    const defaultShadowAlpha = defaultShapes.shadowAlpha;
    const defaultAvatarSizes = defaultShapes.avatarSizes;
    const defaultElevations = defaultShapes.elevations;
    const defaultDurations = defaultShapes.durations;
    const defaultIconSizes = defaultShapes.iconSizes;
    const defaultRadiuses = defaultShapes.radiuses;
    const defaultBorders = defaultShapes.borders;
    const defaultSpaces = defaultShapes.spaces;

    const newShapes: NCoreUIKit.Shapes = {
        controlPaddings: defaultControlPaddings,
        controlHeights: defaultControlHeights,
        inlineSpaces: defaultInlineSpaces,
        shapeConfigs: defaultShapeConfigs,
        touchTargets: defaultTouchTargets,
        avatarSizes: defaultAvatarSizes,
        shadowAlpha: defaultShadowAlpha,
        elevations: defaultElevations,
        durations: defaultDurations,
        iconSizes: defaultIconSizes,
        radiuses: defaultRadiuses,
        borders: defaultBorders,
        spaces: defaultSpaces
    };

    if(!projectShapes) {
        return newShapes;
    }

    if(projectShapes.borders) {
        const projectBorders = projectShapes.borders;

        newShapes.borders = mergeBorders(defaultBorders, projectBorders);
    }

    if(projectShapes.inlineSpaces) {
        const projectInlineSpaces = projectShapes.inlineSpaces;

        newShapes.inlineSpaces = mergeInlineSpaces(defaultInlineSpaces, projectInlineSpaces);
    }

    if(projectShapes.shapeConfigs) {
        const projectShapeConfigs = projectShapes.shapeConfigs;

        newShapes.shapeConfigs = mergeShapeConfigs(defaultShapeConfigs, projectShapeConfigs);
    }

    if(projectShapes.iconSizes) {
        const projectIconSizes = projectShapes.iconSizes;

        newShapes.iconSizes = mergeIconSizes(defaultIconSizes, projectIconSizes);
    }

    if(projectShapes.avatarSizes) {
        const projectAvatarSizes = projectShapes.avatarSizes;

        newShapes.avatarSizes = mergeAvatarSizes(defaultAvatarSizes, projectAvatarSizes);
    }

    if(projectShapes.touchTargets) {
        const projectTouchTargets = projectShapes.touchTargets;

        newShapes.touchTargets = mergeTouchTargets(defaultTouchTargets, projectTouchTargets);
    }

    if(projectShapes.elevations) {
        const projectElevations = projectShapes.elevations;

        newShapes.elevations = mergeElevations(defaultElevations, projectElevations);
    }

    if(projectShapes.shadowAlpha) {
        const projectShadowAlpha = projectShapes.shadowAlpha;

        newShapes.shadowAlpha = mergeShadowAlpha(defaultShadowAlpha, projectShadowAlpha);
    }

    if(projectShapes.durations) {
        const projectDurations = projectShapes.durations;

        newShapes.durations = mergeDurations(defaultDurations, projectDurations);
    }

    if(projectShapes.controlHeights) {
        newShapes.controlHeights = {
            ...defaultControlHeights
        };

        defaultGapPropagations.forEach((gP) => {
            if(projectShapes.controlHeights[gP]) {
                if(defaultControlHeights[gP]) {
                    newShapes.controlHeights[gP] = mergeControlHeights(defaultControlHeights[gP], projectShapes.controlHeights[gP]);
                } else {
                    newShapes.controlHeights[gP] = projectShapes.controlHeights[gP];
                }
            }
        });
    }

    if(projectShapes.controlPaddings) {
        newShapes.controlPaddings = {
            ...defaultControlPaddings
        };

        defaultGapPropagations.forEach((gP) => {
            if(projectShapes.controlPaddings[gP]) {
                if(defaultControlPaddings[gP]) {
                    newShapes.controlPaddings[gP] = mergeControlPaddings(defaultControlPaddings[gP], projectShapes.controlPaddings[gP]);
                } else {
                    newShapes.controlPaddings[gP] = projectShapes.controlPaddings[gP];
                }
            }
        });
    }

    if(projectShapes.spaces) {
        newShapes.spaces = {
            ...defaultSpaces
        };

        defaultGapPropagations.forEach((gP) => {
            if(projectShapes.spaces[gP]) {
                if(defaultSpaces[gP]) {
                    newShapes.spaces[gP] = mergeSpaces(defaultSpaces[gP], projectShapes.spaces[gP]);
                } else {
                    newShapes.spaces[gP] = projectShapes.spaces[gP];
                }
            }
        });
    }

    if(projectShapes.radiuses) {
        newShapes.radiuses = {
            ...defaultRadiuses
        };

        defaultSharpness.forEach((s) => {
            if(projectShapes.radiuses[s]) {
                if(defaultRadiuses[s]) {
                    newShapes.radiuses[s] = mergeRadiuses(defaultRadiuses[s], projectShapes.radiuses[s]);
                } else {
                    newShapes.radiuses[s] = projectShapes.radiuses[s];
                }
            }
        });
    }

    return newShapes;
};
